import { Col, Form, notification, Row } from 'antd';
import { memo, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import ButtonWrap from '@/components/ButtonWrap';
import FormItemWrap from '@/components/FormItemWrap';
import InputWrap from '@/components/InputWrap';
import { pattern } from '@/constants/validate';
import { appRoutes } from '@/router/routes';
import ThuChiService from '@/services/thu_chi.service';
import { handleMessageError } from '@/util/handleMessageError';
import InputMoney from '@/components/InputMoney';
import RadioWrap from '@/components/RadioWrap';
import { ThuChiDTO } from '@/dto/responseDto/thuChi.dto';

const FormData = () => {
    const yesNoOptions = [
        {
            id: 0,
            value: 'Chi',
        },
        {
            id: 1,
            value: 'Thu',
        },
    ];

    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const {
        control,
        reset,
        setValue,
        formState: { errors },
        handleSubmit,
        clearErrors,
    } = useForm();

    const { data: ThuChuItem } = useQuery(['thu_chi', id], () => ThuChiService.getDetail(String(id)), {
        enabled: !!id,
        keepPreviousData: false,
        onError: (error: any) => {
            handleMessageError(error);
        },
    });

    const { mutate: mutateCreate, isLoading: isLoadingCreate } = useMutation(
        (e: ThuChiDTO) => ThuChiService.createThuChi(e),
        {
            onSuccess: (data: any) => {
                onGoList();
                queryClient.removeQueries('thu_chi');
                notification.success({
                    message: 'THÊM THU/CHI THÀNH CÔNG',
                });
            },
            onError: (error: any) => {
                handleMessageError(error);
            },
        },
    );

    const { mutate: mutateUpdate, isLoading: isLoadingUpdate } = useMutation(
        (e: ThuChiDTO) => ThuChiService.update(String(id), e),
        {
            onSuccess: (data: any) => {
                onGoList();
                queryClient.removeQueries('thu_chi');
                notification.success({
                    message: 'CẬP NHẬT THÀNH CÔNG',
                });
            },
            onError: (error: any) => {
                handleMessageError(error);
            },
        },
    );

    const onGoList = () => {
        reset({});
        navigate(`${appRoutes.ADMIN.THU_CHI.ROOT}`, { replace: true });
    };

    const onSubmit = (data: any) => {
        const body: ThuChiDTO = {
            name: data.name,
            is_thu: data.is_thu == 1,
            price: data.price,
            note: data.note,
        };
        id ? mutateUpdate(body) : mutateCreate(body);
    };

    useEffect(() => {
        if (ThuChuItem) {
            reset({
                name: ThuChuItem?.data?.name,
                is_thu: ThuChuItem?.data?.is_thu == true ? 1 : 0,
                price: ThuChuItem?.data?.price,
                note: ThuChuItem?.data?.note,
            });
        }
    }, [ThuChuItem, reset]);

    return (
        <Form labelCol={{ span: 6 }} onFinish={handleSubmit(onSubmit)}>
            <Col span={24} xl={{ span: 16, offset: 4 }} xxl={{ span: 12, offset: 6 }}>
                <FormItemWrap label={'NỘI DUNG THU/CHI'} required validateStatus={errors?.name ? 'error' : ''}>
                    <Controller
                        name="name"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => <InputWrap field={field} />}
                    />
                </FormItemWrap>
                <FormItemWrap
                    labelAlign="right"
                    label="Số tiền"
                    required
                    colon={false}
                    validateStatus={errors?.salesPrice ? 'error' : ''}
                >
                    <Controller
                        name="price"
                        control={control}
                        rules={{
                            required: true,
                            pattern: pattern.inputNumber,
                        }}
                        render={({ field }) => (
                            <InputMoney textAlign="right" addonAfter="₫" placeholder="0" maxLength={12} field={field} />
                        )}
                    />
                </FormItemWrap>
                <FormItemWrap label="Loại" validateStatus={errors?.is_thu ? 'error' : ''} colon={false}>
                    <Controller
                        name="is_thu"
                        control={control}
                        render={({ field }) => <RadioWrap field={field} options={yesNoOptions} defaultValue={0} />}
                    />
                </FormItemWrap>
                <FormItemWrap label={'Ghi chú'} validateStatus={errors?.note ? 'error' : ''}>
                    <Controller name="note" control={control} render={({ field }) => <InputWrap field={field} />} />
                </FormItemWrap>
                <Row justify="end" gutter={16}>
                    <Col>
                        <ButtonWrap
                            isLoading={isLoadingCreate || isLoadingUpdate}
                            htmlType="submit"
                            text={!id ? 'THÊM MỚI' : 'CẬP NHẬT'}
                            type="primary"
                            width="140px"
                        />
                    </Col>
                </Row>
            </Col>
        </Form>
    );
};

export default memo(FormData);
